import { getReportLanguage } from "../utils/reportLanguage";
import { filenameFromContentDisposition } from "./contentDisposition";
import { productionSceneRequest } from "./productionScene";
import { httpResponseError } from "./networkErrors";
// src/services/api.js
import { supabase } from "../lib/supabase";

const BASE_URL = process.env.REACT_APP_API_BASE?.replace(/\/$/, "");

async function resolveAccessToken(accessToken) {
  if (accessToken) return accessToken;

  const { data, error } = await supabase.auth.getSession();
  if (error) {
    console.error("Failed to resolve auth session for API request:", error);
    return null;
  }

  return data?.session?.access_token || null;
}

export async function buildHeaders(accessToken, extraHeaders = {}) {
  const token = await resolveAccessToken(accessToken);
  return {
    ...extraHeaders,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

function extractStreamTicket(payload) {
  if (typeof payload === "string" && payload.trim()) return payload.trim();
  const ticket = payload?.stream_ticket ?? payload?.streamTicket ?? payload?.ticket;
  if (!ticket) throw new Error("Missing stream ticket");
  return String(ticket);
}

async function requestStreamTicket(path, accessToken) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");

  const res = await fetch(`${BASE_URL}${path}`, {
    method: "POST",
    headers: await buildHeaders(accessToken, {
      "Content-Type": "application/json",
    }),
  });

  const text = await res.text().catch(() => "");
  if (!res.ok) {
    const err = new Error(text || `HTTP ${res.status}`);
    err.status = res.status;
    throw err;
  }

  let payload = text;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = text;
    }
  }
  return extractStreamTicket(payload);
}

export function requestRunsStreamTicket(projectId, accessToken) {
  if (!projectId) throw new Error("projectId is required");
  return requestStreamTicket(
    `/runs/stream-ticket?project_id=${encodeURIComponent(String(projectId))}`,
    accessToken
  );
}

export function requestRunStreamTicket(runId, accessToken) {
  if (!runId) throw new Error("runId is required");
  return requestStreamTicket(`/runs/${encodeURIComponent(String(runId))}/stream-ticket`, accessToken);
}

export function requestFeasibilityStreamTicket(feasibilityId, accessToken) {
  if (!feasibilityId) throw new Error("feasibilityId is required");
  return requestStreamTicket(
    `/feasibility/${encodeURIComponent(String(feasibilityId))}/stream-ticket`,
    accessToken
  );
}

async function fetchJson(path, accessToken) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");

  const res = await fetch(`${BASE_URL}${path}`, {
    headers: await buildHeaders(accessToken, {
      "Content-Type": "application/json",
    }),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `HTTP ${res.status}`);
  }

  return res.json();
}

export function getModules(q = "", accessToken) {
  const qs = q ? `?q=${encodeURIComponent(q)}` : "";
  return fetchJson(`/catalog/modules${qs}`, accessToken);
}

export function getInverters(q = "", accessToken) {
  const qs = q ? `?q=${encodeURIComponent(q)}` : "";
  return fetchJson(`/catalog/inverters${qs}`, accessToken);
}

export async function getBatteries(params = {}, accessToken) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");

  const url = new URL(`${BASE_URL}/catalog/batteries`);
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      url.searchParams.set(key, String(value));
    }
  });

  const res = await fetch(url.toString(), {
    headers: await buildHeaders(accessToken, {
      "Content-Type": "application/json",
    }),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `HTTP ${res.status}`);
  }

  return res.json(); // [{ name, energy, p_max_c, p_max_d }, ...]
}

export async function runEngine(payload, accessToken) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");

  const url = `${BASE_URL}/runs/run_engine`;

  const res = await fetch(url, {
    method: "POST",
    headers: await buildHeaders(accessToken, {
      "Content-Type": "application/json",
    }),
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    const errorText = await res.text().catch(() => "");
    let errorData = null;
    if (errorText) {
      try {
        errorData = JSON.parse(errorText);
      } catch {
        errorData = null;
      }
    }
    const error = new Error(errorText || `HTTP ${res.status}`);
    error.status = res.status;
    error.responseText = errorText;
    error.data = errorData;
    throw error;
  }

  return res.json();
}

export async function runAutoDesign(projectId, payload, accessToken) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");
  if (!projectId) throw new Error("projectId is required");

  const url = `${BASE_URL}/design/autodesign/project/${encodeURIComponent(projectId)}`;

  const res = await fetch(url, {
    method: "POST",
    headers: await buildHeaders(accessToken, {
      "Content-Type": "application/json",
    }),
    body: JSON.stringify(payload || {}),
  });

  if (!res.ok) {
    const errorText = await res.text().catch(() => "");
    let errorData = null;
    if (errorText) {
      try {
        errorData = JSON.parse(errorText);
      } catch {
        errorData = null;
      }
    }
    const error = new Error(errorText || `HTTP ${res.status}`);
    error.status = res.status;
    error.responseText = errorText;
    error.data = errorData;
    throw error;
  }

  return res.json();
}

export async function optimizeOrientation(payload, accessToken) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");

  const res = await fetch(`${BASE_URL}/design/optimize-orientation`, {
    method: "POST",
    headers: await buildHeaders(accessToken, {
      "Content-Type": "application/json",
    }),
    body: JSON.stringify(payload || {}),
  });

  if (!res.ok) {
    const errorText = await res.text().catch(() => "");
    throw new Error(errorText || `HTTP ${res.status}`);
  }

  return res.json();
}

export async function downloadAutoDesignReportExcel(projectId, accessToken, reportId = null) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");
  if (!projectId) throw new Error("projectId is required");

  const reportQuery = reportId ? `?report_id=${encodeURIComponent(reportId)}` : "";
  const res = await fetch(`${BASE_URL}/design/autodesign/${encodeURIComponent(projectId)}/download-report-excel${reportQuery}`, {
    headers: await buildHeaders(accessToken),
  });

  if (!res.ok) {
    const msg = await res.text().catch(() => "");
    throw new Error(msg || `Design report download failed (${res.status})`);
  }

  const blob = await res.blob();
  const filename =
    filenameFromContentDisposition(res.headers?.get("content-disposition")) ||
    "Solar_Design_Report.xlsx";

  return { blob, filename };
}

export async function listAutoDesignReports(projectId, accessToken) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");
  if (!projectId) throw new Error("projectId is required");

  const res = await fetch(`${BASE_URL}/design/autodesign/${encodeURIComponent(projectId)}/reports`, {
    headers: await buildHeaders(accessToken),
  });
  if (!res.ok) throw new Error(`Could not load auto-design reports (${res.status})`);
  const data = await res.json();
  return Array.isArray(data?.reports) ? data.reports : [];
}

export async function exportBessExcel(payload, accessToken) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");

  const res = await fetch(`${BASE_URL}/bess/optimize/export-excel`, {
    method: "POST",
    headers: await buildHeaders(accessToken, {
      "Content-Type": "application/json",
    }),
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    const msg = await res.text().catch(() => "");
    throw new Error(msg || `Excel export failed (${res.status})`);
  }

  const blob = await res.blob();
  const filename =
    filenameFromContentDisposition(res.headers?.get("content-disposition")) ||
    "opt_results.xlsx";

  return { blob, filename };
}

export async function exportBidScheduleExcel(payload, accessToken) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");

  const res = await fetch(`${BASE_URL}/bid-schedules/export-excel`, {
    method: "POST",
    headers: await buildHeaders(accessToken, {
      "Content-Type": "application/json",
    }),
    body: JSON.stringify(payload || {}),
  });

  if (!res.ok) {
    const msg = await res.text().catch(() => "");
    throw new Error(msg || `Bid schedule export failed (${res.status})`);
  }

  const blob = await res.blob();
  const filename =
    filenameFromContentDisposition(res.headers?.get("content-disposition")) ||
    "bid_schedule.xlsx";

  return { blob, filename };
}

export async function getBidSchedule(scope, accessToken) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");

  const projectId = scope?.project_id ?? scope?.projectId;
  const assetId = scope?.asset_id ?? scope?.assetId ?? scope?.assetKey;
  const bidDay = scope?.bid_day ?? scope?.bidDay;
  const market = scope?.market;

  if (!projectId || !assetId || !bidDay) {
    throw new Error("project_id, asset_id, and bid_day are required");
  }

  const url = new URL(`${BASE_URL}/projects/${encodeURIComponent(String(projectId))}/bid-schedules`);
  if (assetId) url.searchParams.set("asset_id", String(assetId));
  if (bidDay) url.searchParams.set("bid_day", String(bidDay));
  if (market) url.searchParams.set("market", String(market));

  const res = await fetch(url.toString(), {
    headers: await buildHeaders(accessToken, {
      "Content-Type": "application/json",
    }),
  });

  if (res.status === 404) {
    return null;
  }
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `HTTP ${res.status}`);
  }

  return res.json().catch(() => null);
}

export async function listBidSchedules(scope, accessToken) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");

  const projectId = scope?.project_id ?? scope?.projectId;
  const assetId = scope?.asset_id ?? scope?.assetId ?? scope?.assetKey;
  const market = scope?.market;

  if (!projectId) {
    throw new Error("project_id is required");
  }

  const candidateUrls = [
    new URL(`${BASE_URL}/projects/${encodeURIComponent(String(projectId))}/bid-schedules/history`),
    new URL(`${BASE_URL}/projects/${encodeURIComponent(String(projectId))}/bid-schedules`),
    new URL(`${BASE_URL}/bid-schedules`),
  ];

  for (const url of candidateUrls) {
    if (assetId) url.searchParams.set("asset_id", String(assetId));
    if (market) url.searchParams.set("market", String(market));
    url.searchParams.set("include_rows", "true");
  }

  for (const url of candidateUrls) {
    let res;
    try {
      res = await fetch(url.toString(), {
        headers: await buildHeaders(accessToken, {
          "Content-Type": "application/json",
        }),
      });
    } catch {
      continue;
    }

    if ([400, 403, 404, 405, 422, 500, 501].includes(res.status)) continue;
    if (!res.ok) {
      continue;
    }

    const payload = await res.json().catch(() => null);
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.items)) return payload.items;
    if (Array.isArray(payload?.schedules)) return payload.schedules;
    if (Array.isArray(payload?.data)) return payload.data;
    if (payload && typeof payload === "object") return [payload];
    return [];
  }

  return [];
}

export async function getBidScheduleSettings(scope, accessToken) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");

  const projectId = scope?.project_id ?? scope?.projectId;
  const assetId = scope?.asset_id ?? scope?.assetId ?? scope?.assetKey;
  if (!projectId || !assetId) {
    throw new Error("project_id and asset_id are required");
  }

  const url = new URL(`${BASE_URL}/bid-schedules/settings`);
  url.searchParams.set("project_id", String(projectId));
  url.searchParams.set("asset_id", String(assetId));

  const res = await fetch(url.toString(), {
    headers: await buildHeaders(accessToken, {
      "Content-Type": "application/json",
    }),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `HTTP ${res.status}`);
  }

  return res.json();
}

export async function getLatestBatteryState(scope, accessToken) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");

  const projectId = scope?.project_id ?? scope?.projectId;
  const assetId = scope?.asset_id ?? scope?.assetId ?? scope?.assetKey;
  const market = scope?.market;

  if (!projectId || !assetId) {
    throw new Error("project_id and asset_id are required");
  }

  const url = new URL(
    `${BASE_URL}/projects/${encodeURIComponent(String(projectId))}/battery-states/latest`
  );
  url.searchParams.set("asset_id", String(assetId));
  if (market) url.searchParams.set("market", String(market));

  const res = await fetch(url.toString(), {
    headers: await buildHeaders(accessToken, {
      "Content-Type": "application/json",
    }),
  });

  if (res.status === 404) {
    return null;
  }
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `HTTP ${res.status}`);
  }

  return res.json().catch(() => null);
}

export async function getBatteryStateHistory(scope, accessToken) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");

  const projectId = scope?.project_id ?? scope?.projectId;
  const assetId = scope?.asset_id ?? scope?.assetId ?? scope?.assetKey;
  const market = scope?.market;
  const startDate = scope?.start_date ?? scope?.startDate;
  const endDate = scope?.end_date ?? scope?.endDate;

  if (!projectId || !assetId) {
    throw new Error("project_id and asset_id are required");
  }

  const url = new URL(
    `${BASE_URL}/projects/${encodeURIComponent(String(projectId))}/battery-states/history`
  );
  url.searchParams.set("asset_id", String(assetId));
  if (market) url.searchParams.set("market", String(market));
  if (startDate) url.searchParams.set("start_date", String(startDate));
  if (endDate) url.searchParams.set("end_date", String(endDate));

  const res = await fetch(url.toString(), {
    headers: await buildHeaders(accessToken, {
      "Content-Type": "application/json",
    }),
  });

  if (res.status === 404) {
    return { items: [] };
  }
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `HTTP ${res.status}`);
  }

  return res.json().catch(() => ({ items: [] }));
}

export async function saveBidScheduleSettings(projectId, assetId, settings, accessToken) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");
  if (!projectId || !assetId) throw new Error("projectId and assetId are required");

  const res = await fetch(`${BASE_URL}/bid-schedules/settings`, {
    method: "PUT",
    headers: await buildHeaders(accessToken, {
      "Content-Type": "application/json",
    }),
    body: JSON.stringify({
      project_id: String(projectId),
      asset_id: String(assetId),
      settings: settings || {},
    }),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `HTTP ${res.status}`);
  }

  return res.json();
}

export async function updateBidScheduleSettings(scheduleId, settings, accessToken) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");
  if (!scheduleId) throw new Error("scheduleId is required");

  const res = await fetch(`${BASE_URL}/bid-schedules/${encodeURIComponent(scheduleId)}/settings`, {
    method: "PUT",
    headers: await buildHeaders(accessToken, {
      "Content-Type": "application/json",
    }),
    body: JSON.stringify({ settings: settings || {} }),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `HTTP ${res.status}`);
  }

  return res.json();
}

export async function refreshBidSchedule(scheduleId, accessToken, runId) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");
  if (!scheduleId) throw new Error("scheduleId is required");

  const url = new URL(`${BASE_URL}/bid-schedules/${encodeURIComponent(scheduleId)}/refresh`);
  if (runId) url.searchParams.set("run_id", String(runId));

  const res = await fetch(url.toString(), {
    method: "POST",
    headers: await buildHeaders(accessToken, {
      "Content-Type": "application/json",
    }),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `HTTP ${res.status}`);
  }

  return res.json();
}

export async function updateBidScheduleRows(scheduleId, rows, accessToken) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");
  if (!scheduleId) throw new Error("scheduleId is required");

  const res = await fetch(`${BASE_URL}/bid-schedules/${encodeURIComponent(scheduleId)}`, {
    method: "PUT",
    headers: await buildHeaders(accessToken, {
      "Content-Type": "application/json",
    }),
    body: JSON.stringify({ rows: Array.isArray(rows) ? rows : [] }),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `HTTP ${res.status}`);
  }

  return res.json();
}

export async function upsertBidSchedule(scope, schedule, accessToken) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");

  const body = {
    ...(scope || {}),
    ...(schedule || {}),
  };

  const res = await fetch(`${BASE_URL}/bid-schedules`, {
    method: "PUT",
    headers: await buildHeaders(accessToken, {
      "Content-Type": "application/json",
    }),
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `HTTP ${res.status}`);
  }

  return res.json();
}

export async function getLegacyBidSchedule(scope, accessToken) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");

  const url = new URL(`${BASE_URL}/bid-schedules`);
  Object.entries(scope || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      url.searchParams.set(key, String(value));
    }
  });

  const res = await fetch(url.toString(), {
    headers: await buildHeaders(accessToken, {
      "Content-Type": "application/json",
    }),
  });

  if (res.status === 404) {
    return null;
  }
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `HTTP ${res.status}`);
  }

  return res.json().catch(() => null);
}

export async function submitBidSchedule(scheduleId, accessToken) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");
  if (!scheduleId) throw new Error("scheduleId is required");

  const res = await fetch(`${BASE_URL}/bid-schedules/${encodeURIComponent(scheduleId)}/submit`, {
    method: "POST",
    headers: await buildHeaders(accessToken, {
      "Content-Type": "application/json",
    }),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `HTTP ${res.status}`);
  }

  return res.json();
}

export async function reopenBidSchedule(scheduleId, accessToken) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");
  if (!scheduleId) throw new Error("scheduleId is required");

  const res = await fetch(`${BASE_URL}/bid-schedules/${encodeURIComponent(scheduleId)}/reopen`, {
    method: "POST",
    headers: await buildHeaders(accessToken, {
      "Content-Type": "application/json",
    }),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `HTTP ${res.status}`);
  }

  return res.json();
}

export async function exportBidScheduleCsv(scheduleId, accessToken) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");
  if (!scheduleId) throw new Error("scheduleId is required");

  const res = await fetch(`${BASE_URL}/bid-schedules/${encodeURIComponent(scheduleId)}/export-csv`, {
    method: "POST",
    headers: await buildHeaders(accessToken, {
      "Content-Type": "application/json",
    }),
  });

  if (!res.ok) {
    const msg = await res.text().catch(() => "");
    throw new Error(msg || `Bid schedule export failed (${res.status})`);
  }

  const blob = await res.blob();
  const filename =
    filenameFromContentDisposition(res.headers?.get("content-disposition")) ||
    "bid_schedule.csv";

  return { blob, filename };
}

export async function exportEconomicsOverviewExcel(scope, accessToken) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");

  const projectId = scope?.project_id ?? scope?.projectId;
  const assetId = scope?.asset_id ?? scope?.assetId ?? scope?.assetKey;
  const market = scope?.market;
  const startDate = scope?.start_date ?? scope?.startDate;
  const endDate = scope?.end_date ?? scope?.endDate;

  if (!projectId || !assetId || !market || !startDate || !endDate) {
    throw new Error("project_id, asset_id, market, start_date, and end_date are required");
  }

  const url = new URL(
    `${BASE_URL}/projects/${encodeURIComponent(String(projectId))}/bid-schedules/economics-overview/export-excel`
  );
  url.searchParams.set("asset_id", String(assetId));
  url.searchParams.set("market", String(market));
  url.searchParams.set("start_date", String(startDate));
  url.searchParams.set("end_date", String(endDate));

  const res = await fetch(url.toString(), {
    headers: await buildHeaders(accessToken, {
      Accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }),
  });

  if (!res.ok) {
    const msg = await res.text().catch(() => "");
    throw new Error(msg || `Economics overview export failed (${res.status})`);
  }

  const blob = await res.blob();
  const filename =
    filenameFromContentDisposition(res.headers?.get("content-disposition")) ||
    "economics_overview.xlsx";

  return {
    blob: new Blob([blob], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }),
    filename,
  };
}

/**
 * Standard UTC offset for a coordinate, for putting a wall clock on a sun angle.
 *
 * The shading viewer computes solar position in the browser, which needs no
 * network - but turning that into "09:00" needs the project's timezone, and
 * there is no browser API from a coordinate to an IANA zone. The offset is the
 * STANDARD one, without daylight saving, matching the time base the simulation
 * runs on. Callers should treat a failure as non-fatal and fall back to the
 * zone meridian for the longitude; being an hour out on the label is a much
 * smaller problem than a viewer that will not open.
 */
export async function fetchUtcOffset(lat, lon, accessToken) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");
  const res = await fetch(
    `${BASE_URL}/geo/utc-offset?lat=${encodeURIComponent(lat)}&lon=${encodeURIComponent(lon)}`,
    { headers: await buildHeaders(accessToken) }
  );
  if (!res.ok) throw new Error(`Timezone lookup failed (${res.status})`);
  return res.json();
}

/**
 * The weather a completed run actually used, as the backend reported it.
 *
 * Read from response headers rather than assumed on this side: the engine
 * picks its source per call site, and a caption that states it from memory is
 * a caption that will one day be wrong. Returns null when the headers are
 * absent - an older backend, or a run that kept no record.
 *
 * Requires X-Weather-Source* in the API's CORS expose_headers.
 */
export function readWeatherSource(res) {
  const label = res?.headers?.get("x-weather-source") || "";
  const detail = res?.headers?.get("x-weather-source-detail") || "";
  if (!label && !detail) return null;
  return { label, detail };
}

export async function exportPvGeneration(config, accessToken) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");

  const res = await fetch(`${BASE_URL}/pv/export-generation`, {
    method: "POST",
    headers: await buildHeaders(accessToken, {
      "Content-Type": "application/json",
    }),
    body: JSON.stringify({ config }),
  });

  if (!res.ok) {
    const msg = await res.text().catch(() => "");
    throw new Error(msg || `PV export failed (${res.status})`);
  }

  const blob = await res.blob();
  const filename =
    filenameFromContentDisposition(res.headers?.get("content-disposition")) ||
    "pv_generation_overview.xlsx";

  return { blob, filename, weatherSource: readWeatherSource(res) };
}

export async function downloadLatestPvDetails(projectId, accessToken) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");
  if (!projectId) throw new Error("projectId is required");

  const res = await fetch(
    `${BASE_URL}/pv-details/${encodeURIComponent(projectId)}/download`,
    {
      headers: await buildHeaders(accessToken),
    }
  );

  if (res.status === 404) {
    const err = new Error("PV details report is not available yet");
    err.status = 404;
    throw err;
  }

  if (!res.ok) {
    const msg = await res.text().catch(() => "");
    const err = new Error(msg || `PV details download failed (${res.status})`);
    err.status = res.status;
    throw err;
  }

  const blob = await res.blob();
  const filename =
    filenameFromContentDisposition(res.headers?.get("content-disposition")) ||
    `${projectId}_pv_details.xlsx`;

  return { blob, filename };
}

export async function triggerPvGenerationRun(
  { projectId, start_date, end_date, tz, runType },
  accessToken
) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");
  if (!projectId) throw new Error("projectId is required");
  if (!start_date || !end_date) throw new Error("start_date and end_date are required");
  if (!runType) throw new Error("runType is required");

  const res = await fetch(
    `${BASE_URL}/projects/${encodeURIComponent(projectId)}/pv/generation/runs`,
    {
      method: "POST",
      headers: await buildHeaders(accessToken, {
        "Content-Type": "application/json",
      }),
      body: JSON.stringify({ start_date, end_date, tz, run_type: runType }),
    }
  );

  if (!res.ok) {
    const msg = await res.text().catch(() => "");
    const err = new Error(msg || `Failed to trigger PV generation run (${res.status})`);
    err.status = res.status;
    throw err;
  }

  return res.json(); // { run_id, run_type, start_date, end_date, status, created_at }
}

export async function fetchPvGenerationRuns(
  { projectId, start_date, end_date, runType },
  accessToken
) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");
  if (!projectId) throw new Error("projectId is required");

  const url = new URL(
    `${BASE_URL}/projects/${encodeURIComponent(projectId)}/pv/generation/runs`
  );
  if (start_date) url.searchParams.set("start_date", start_date);
  if (end_date) url.searchParams.set("end_date", end_date);
  if (runType) url.searchParams.set("run_type", runType);

  const res = await fetch(url.toString(), {
    headers: await buildHeaders(accessToken, {
      "Content-Type": "application/json",
    }),
  });

  if (!res.ok) {
    const msg = await res.text().catch(() => "");
    const err = new Error(msg || `Failed to fetch PV generation runs (${res.status})`);
    err.status = res.status;
    throw err;
  }

  const payload = await res.json();
  return Array.isArray(payload) ? payload : payload?.runs || [];
}

export async function fetchPvGenerationRunSeries({ projectId, runId }, accessToken) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");
  if (!projectId) throw new Error("projectId is required");
  if (!runId) throw new Error("runId is required");

  const res = await fetch(
    `${BASE_URL}/projects/${encodeURIComponent(projectId)}/pv/generation/runs/${encodeURIComponent(runId)}`,
    {
      headers: await buildHeaders(accessToken, {
        "Content-Type": "application/json",
      }),
    }
  );

  if (!res.ok) {
    const msg = await res.text().catch(() => "");
    const err = new Error(msg || `Failed to fetch PV generation run (${res.status})`);
    err.status = res.status;
    throw err;
  }

  return res.json(); // { run_id, run_type, status, ..., series: [{ time, generation_kw }] }
}

export async function triggerWindGenerationRun(
  { projectId, start_date, end_date, tz, runType },
  accessToken
) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");
  if (!projectId) throw new Error("projectId is required");
  if (!start_date || !end_date) throw new Error("start_date and end_date are required");
  if (!runType) throw new Error("runType is required");

  const res = await fetch(
    `${BASE_URL}/projects/${encodeURIComponent(projectId)}/wind/generation/runs`,
    {
      method: "POST",
      headers: await buildHeaders(accessToken, {
        "Content-Type": "application/json",
      }),
      body: JSON.stringify({ start_date, end_date, tz, run_type: runType }),
    }
  );

  if (!res.ok) {
    const msg = await res.text().catch(() => "");
    const err = new Error(msg || `Failed to trigger wind generation run (${res.status})`);
    err.status = res.status;
    throw err;
  }

  return res.json(); // { run_id, run_type, start_date, end_date, status, created_at }
}

export async function fetchWindGenerationRuns(
  { projectId, start_date, end_date, runType },
  accessToken
) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");
  if (!projectId) throw new Error("projectId is required");

  const url = new URL(
    `${BASE_URL}/projects/${encodeURIComponent(projectId)}/wind/generation/runs`
  );
  if (start_date) url.searchParams.set("start_date", start_date);
  if (end_date) url.searchParams.set("end_date", end_date);
  if (runType) url.searchParams.set("run_type", runType);

  const res = await fetch(url.toString(), {
    headers: await buildHeaders(accessToken, {
      "Content-Type": "application/json",
    }),
  });

  if (!res.ok) {
    const msg = await res.text().catch(() => "");
    const err = new Error(msg || `Failed to fetch wind generation runs (${res.status})`);
    err.status = res.status;
    throw err;
  }

  const payload = await res.json();
  return Array.isArray(payload) ? payload : payload?.runs || [];
}

export async function fetchWindGenerationRunSeries({ projectId, runId }, accessToken) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");
  if (!projectId) throw new Error("projectId is required");
  if (!runId) throw new Error("runId is required");

  const res = await fetch(
    `${BASE_URL}/projects/${encodeURIComponent(projectId)}/wind/generation/runs/${encodeURIComponent(runId)}`,
    {
      headers: await buildHeaders(accessToken, {
        "Content-Type": "application/json",
      }),
    }
  );

  if (!res.ok) {
    const msg = await res.text().catch(() => "");
    const err = new Error(msg || `Failed to fetch wind generation run (${res.status})`);
    err.status = res.status;
    throw err;
  }

  return res.json(); // { run_id, run_type, status, ..., series: [{ time, generation_kw }] }
}

/**
 * @typedef {Object} TurbineCatalogQuery
 * @property {number|string=} min_nominal_power_kw
 * @property {number|string=} max_nominal_power_kw
 * @property {string=} search
 */

/**
 * @typedef {Object} TurbineCatalogItem
 * @property {string} name
 * @property {string|null=} manufacturer
 * @property {number|null=} nominal_power_kw
 * @property {number|null=} rotor_diameter_m
 * @property {number|null=} default_hub_height_m
 */

/**
 * @typedef {Object} TurbineCurvePoint
 * @property {number} x
 * @property {number} y
 */

/**
 * @typedef {Object} TurbineCurve
 * @property {string} key
 * @property {string=} label
 * @property {string=} x_label
 * @property {string=} y_label
 * @property {number=} point_count
 * @property {TurbineCurvePoint[]=} points
 */

/**
 * @typedef {Object} TurbineDetailsResponse
 * @property {string} name
 * @property {string|null=} manufacturer
 * @property {number|null=} nominal_power_kw
 * @property {number|null=} rotor_diameter_m
 * @property {number|null=} default_hub_height_m
 * @property {TurbineCurve[]=} curves
 * @property {Array<Object>=} display_sections
 * @property {Object<string, unknown>=} characteristics
 */

/**
 * @typedef {Object} SaveTurbineRequest
 * @property {string} turbine_name
 * @property {{
 *   manufacturer?: string | null,
 *   nominal_power_kw: number,
 *   rotor_diameter_m?: number | null,
 *   default_hub_height_m?: number | null,
 *   power_curve: { wind_speeds_ms: number[], power_w: number[] },
 *   power_coefficient_curve?: { wind_speeds_ms: number[], cp_values: number[] }
 * }} characteristics
 */

/**
 * @typedef {Object} SaveTurbineResponse
 * @property {string} catalog
 * @property {string} name
 * @property {Object<string, unknown>} characteristics
 * @property {string=} action
 */

/**
 * @param {TurbineCatalogQuery} params
 * @param {string | null | undefined} accessToken
 * @returns {Promise<TurbineCatalogItem[]>}
 */
export async function getTurbines(params = {}, accessToken) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");

  const url = new URL(`${BASE_URL}/catalog/turbines`);
  if (params.min_nominal_power_kw != null) {
    url.searchParams.set("min_nominal_power_kw", params.min_nominal_power_kw);
  }
  if (params.max_nominal_power_kw != null) {
    url.searchParams.set("max_nominal_power_kw", params.max_nominal_power_kw);
  }
  if (params.search) url.searchParams.set("search", params.search);

  const res = await fetch(url.toString(), {
    headers: await buildHeaders(accessToken, {
      "Content-Type": "application/json",
    }),
  });

  if (!res.ok) {
    const msg = await res.text().catch(() => "");
    const err = new Error(msg || `Failed to fetch turbines (${res.status})`);
    err.status = res.status;
    throw err;
  }

  return res.json(); // [{ name, manufacturer, nominal_power_kw, rotor_diameter_m }]
}

export const listTurbines = getTurbines;

/**
 * @param {string} name
 * @param {string | null | undefined} accessToken
 * @returns {Promise<TurbineDetailsResponse>}
 */
export async function getTurbineDetails(name, accessToken) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");
  if (!name) throw new Error("name is required");

  const url = new URL(`${BASE_URL}/catalog/turbine_details`);
  url.searchParams.set("name", name);

  const res = await fetch(url.toString(), {
    headers: await buildHeaders(accessToken, {
      "Content-Type": "application/json",
    }),
  });

  if (!res.ok) {
    const msg = await res.text().catch(() => "");
    const err = new Error(msg || `Failed to fetch turbine details (${res.status})`);
    err.status = res.status;
    throw err;
  }

  return res.json(); // { name, manufacturer, nominal_power_kw, ..., characteristics }
}

/**
 * The backend currently accepts POST /catalog/turbines for both create and update-like
 * custom turbine saves. Keep the client naming explicit so only the transport mapping
 * needs to change if a dedicated update endpoint is added later.
 *
 * @param {SaveTurbineRequest} payload
 * @param {string | null | undefined} accessToken
 * @returns {Promise<SaveTurbineResponse>}
 */
export async function createTurbine(payload, accessToken) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");

  const res = await fetch(`${BASE_URL}/catalog/turbines`, {
    method: "POST",
    headers: await buildHeaders(accessToken, {
      "Content-Type": "application/json",
    }),
    body: JSON.stringify(payload), // { turbine_name, characteristics }
  });

  if (!res.ok) {
    const msg = await res.text().catch(() => "");
    const err = new Error(msg || `Failed to create turbine (${res.status})`);
    err.status = res.status;
    throw err;
  }

  return res.json();
}

export const saveTurbine = createTurbine;

export async function updateTurbine(payload, accessToken) {
  return createTurbine(payload, accessToken);
}

export async function fetchPvMeasuredGeneration(
  { projectId, start_date, end_date },
  accessToken
) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");
  if (!projectId) throw new Error("projectId is required");
  if (!start_date || !end_date) throw new Error("start_date and end_date are required");

  const url = new URL(
    `${BASE_URL}/projects/${encodeURIComponent(projectId)}/pv/generation/measured`
  );
  url.searchParams.set("start_date", start_date);
  url.searchParams.set("end_date", end_date);

  const res = await fetch(url.toString(), {
    headers: await buildHeaders(accessToken, {
      "Content-Type": "application/json",
    }),
  });

  if (!res.ok) {
    const msg = await res.text().catch(() => "");
    const err = new Error(msg || `Failed to fetch measured PV generation (${res.status})`);
    err.status = res.status;
    throw err;
  }

  return res.json(); // { available: boolean, series?: [{ time, generation_kw }] }
}

export async function exportPvGenerationOverview(
  { projectId, runIds, includeMeasured, start_date, end_date },
  accessToken
) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");
  if (!projectId) throw new Error("projectId is required");

  const res = await fetch(
    `${BASE_URL}/projects/${encodeURIComponent(projectId)}/pv/generation/export-excel`,
    {
      method: "POST",
      headers: await buildHeaders(accessToken, {
        "Content-Type": "application/json",
      }),
      body: JSON.stringify({
        run_ids: runIds || [],
        include_measured: !!includeMeasured,
        start_date,
        end_date,
      }),
    }
  );

  if (!res.ok) {
    const msg = await res.text().catch(() => "");
    const err = new Error(msg || `PV generation export failed (${res.status})`);
    err.status = res.status;
    throw err;
  }

  const blob = await res.blob();
  const filename =
    filenameFromContentDisposition(res.headers?.get("content-disposition")) ||
    "pv_generation_overview.xlsx";

  return { blob, filename };
}

export async function getRunStatus(runId, accessToken) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");
  if (!runId) throw new Error("runId is required");

  const res = await fetch(`${BASE_URL}/runs/${encodeURIComponent(runId)}/status`, {
    headers: await buildHeaders(accessToken, {
      "Content-Type": "application/json",
    }),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `HTTP ${res.status}`);
  }

  return res.json();
}

export async function listRunsByProject(projectId, accessToken, options = {}) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");
  if (!projectId) throw new Error("projectId is required");

  const res = await fetch(`${BASE_URL}/runs/by_project/${encodeURIComponent(projectId)}`, {
    headers: await buildHeaders(accessToken, {
      "Content-Type": "application/json",
    }),
    signal: options.signal,
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `HTTP ${res.status}`);
  }

  return res.json();
}

export async function getRunSummary(runId, accessToken, options = {}) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");
  if (!runId) throw new Error("runId is required");

  const res = await fetch(`${BASE_URL}/runs/${encodeURIComponent(runId)}/summary`, {
    headers: await buildHeaders(accessToken, {
      "Content-Type": "application/json",
    }),
    signal: options.signal,
  });

  if (res.status === 404) {
    return null;
  }
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `HTTP ${res.status}`);
  }

  return res.json();
}

export async function saveScheduleSettings(projectId, payload, accessToken) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");
  if (!projectId) throw new Error("projectId is required");

  const res = await fetch(`${BASE_URL}/runs/schedule/${encodeURIComponent(projectId)}`, {
    method: "PUT",
    headers: await buildHeaders(accessToken, {
      "Content-Type": "application/json",
    }),
    body: JSON.stringify(payload || {}),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `HTTP ${res.status}`);
  }

  return res.json().catch(() => null);
}

export async function getScheduleSettings(projectId, accessToken) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");
  if (!projectId) throw new Error("projectId is required");

  const res = await fetch(`${BASE_URL}/runs/schedule/${encodeURIComponent(projectId)}`, {
    headers: await buildHeaders(accessToken, {
      "Content-Type": "application/json",
    }),
  });

  if (res.status === 404) {
    return null;
  }
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `HTTP ${res.status}`);
  }

  return res.json().catch(() => null);
}

export async function updateScheduleInputForecast(projectId, input_forecast, accessToken) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");
  if (!projectId) throw new Error("projectId is required");

  const res = await fetch(`${BASE_URL}/runs/schedule/${encodeURIComponent(projectId)}/input-forecast`, {
    method: "PATCH",
    headers: await buildHeaders(accessToken, {
      "Content-Type": "application/json",
    }),
    body: JSON.stringify({ input_forecast: input_forecast || [] }),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `HTTP ${res.status}`);
  }

  return res.json();
}

export async function getConfigById(configId, accessToken) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");
  if (!configId) throw new Error("configId is required");

  const res = await fetch(`${BASE_URL}/configs/${encodeURIComponent(configId)}`, {
    headers: await buildHeaders(accessToken, {
      "Content-Type": "application/json",
    }),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `HTTP ${res.status}`);
  }

  return res.json(); // { id, project_id, name, config: {...}, ... }
}

const MARKET_METRICS_ENTSOE_URL =
  "https://bess-app-prod-production.up.railway.app/market_metrics_entsoe";

export async function fetchMarketMetrics({ metrics, startDate, endDate, market }, accessToken) {
  if (!metrics || (Array.isArray(metrics) && metrics.length === 0)) {
    throw new Error("metrics is required");
  }
  if (!startDate || !endDate) throw new Error("startDate and endDate are required");
  if (!market) throw new Error("market is required");

  const body = {
    metrics,
    start_date: startDate,
    end_date: endDate,
    market,
  };

  const res = await fetch(MARKET_METRICS_ENTSOE_URL, {
    method: "POST",
    headers: await buildHeaders(accessToken, {
      "Content-Type": "application/json",
      Accept: "application/json",
    }),
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `HTTP ${res.status}`);
  }

  return res.json(); // returns an object keyed by metric -> list of { date, <metric>: value }
}

// The DAM forecast source for market-level views, which have no project and
// therefore no project config to read. Every project is on the in-house MetEn
// forecast, and Forecast Valley is being removed; naming it here rather than at
// each call site means there is exactly one line to change if that ever moves
// again, instead of a string repeated across two pages.
export const DAM_MARKET_FORECAST_PROVIDER = "met_en_internal_published";

// `provider` names the FORECAST source, using the project-config values the
// backend resolver accepts (damForecastProvider / marketForecastSource):
// "met_en_internal_published" or "forecast_valley_legacy_default". OMITTING it
// is the historical behaviour - dam_market_prices, i.e. the legacy Forecast
// Valley column - so callers that do not know which provider they want are
// unaffected. Pass it when the answer matters, which is any time the number is
// compared against what the optimization will actually price with.
// How much of a calendar year a market actually has prices for. One aggregate
// on the server, so the Simulation page can say a reference year is only part
// loaded BEFORE the study runs on it — a gap-priced year completes normally and
// simply earns less, which no results page can distinguish from a weak project.
export async function fetchDamPriceCoverage({ market, year }, accessToken) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");
  const url = new URL(`${BASE_URL}/dam/prices/coverage`);
  url.searchParams.set("market", market);
  url.searchParams.set("year", String(year));
  const res = await fetch(url.toString(), {
    headers: await buildHeaders(accessToken, { "Content-Type": "application/json" }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `HTTP ${res.status}`);
  }
  return res.json();
}

// The average day-ahead price of the study's simulation year, computed from the
// same series the study prices against. It is the level a blank leading year of
// a forward price path is held at; null when the year cannot be priced.
export async function fetchDamAnnualMean({ market, year, tz }, accessToken) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");
  const url = new URL(`${BASE_URL}/dam/prices/annual-mean`);
  url.searchParams.set("market", market);
  url.searchParams.set("year", String(year));
  if (tz) url.searchParams.set("tz", tz);
  const res = await fetch(url.toString(), {
    headers: await buildHeaders(accessToken, { "Content-Type": "application/json" }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `HTTP ${res.status}`);
  }
  return res.json();
}

export async function fetchDamPrices({ market, start, end, series = "both", provider }, accessToken) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");
  if (!market || !start || !end) throw new Error("market, start, and end are required");

  const url = new URL(`${BASE_URL}/dam/prices`);
  url.searchParams.set("market", market);
  url.searchParams.set("start", start);
  url.searchParams.set("end", end);
  url.searchParams.set("series", series);
  if (provider) url.searchParams.set("provider", provider);

  const res = await fetch(url.toString(), {
    headers: await buildHeaders(accessToken, {
      "Content-Type": "application/json",
    }),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `HTTP ${res.status}`);
  }

  const payload = await res.json(); // expected: { data: [...], metrics: {...} }
  if (Array.isArray(payload)) {
    return { data: payload, metrics: null, forecastProvider: null, forecastSource: null, forecastMode: null };
  }
  return {
    data: payload?.data || [],
    metrics: payload?.metrics || null,
    // Present only when `provider` was sent. forecastMode says how that
    // provider's published forecast was actually built: cached weather, proxy
    // RES, proxy load, or a TSO RES total instead of the wind/solar split.
    forecastProvider: payload?.forecast_provider || null,
    forecastSource: payload?.forecast_source || null,
    forecastMode: payload?.forecast_mode || null,
  };
}

export async function fetchBalancingData(
  { market, start, end, datasets },
  accessToken,
  { signal } = {}
) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");
  if (!market || !start || !end) throw new Error("market, start, and end are required");

  const url = new URL(`${BASE_URL}/balancing/data`);
  url.searchParams.set("market", market);
  url.searchParams.set("start", start);
  url.searchParams.set("end", end);
  if (Array.isArray(datasets)) {
    datasets.forEach((dataset) => {
      if (dataset) url.searchParams.append("datasets", dataset);
    });
  }

  const res = await fetch(url.toString(), {
    headers: await buildHeaders(accessToken, {
      "Content-Type": "application/json",
    }),
    signal,
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    const error = new Error(text || `HTTP ${res.status}`);
    error.status = res.status;
    throw error;
  }

  // expected: { market, start, end, counts, data: { <dataset>: [...] } }
  return res.json();
}

export async function fetchBalancingForecastLatest(
  { market, deliveryDate },
  accessToken,
  { signal } = {}
) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");
  if (!market) throw new Error("market is required");

  const url = new URL(`${BASE_URL}/balancing/forecast/latest`);
  url.searchParams.set("market", market);
  if (deliveryDate) url.searchParams.set("delivery_date", deliveryDate);

  const res = await fetch(url.toString(), {
    headers: await buildHeaders(accessToken, {
      "Content-Type": "application/json",
    }),
    signal,
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    const error = new Error(text || `HTTP ${res.status}`);
    error.status = res.status;
    throw error;
  }

  // expected: { provider, market, delivery_date, series: { <series_key>: {...rows} }, missing_series }
  return res.json();
}

// p24: the same published forecast PLUS the realised actual on the same
// intervals and the stored score. Separate from fetchBalancingForecastLatest
// rather than replacing it: /latest answers all seven series in one query and
// stays the panel's primary read, while this is called for the ONE selected
// series, so a panel that draws six series' worth of nothing does not pay for
// twenty-eight queries. If this call fails the panel still has its forecast.
//
// Rows carry `actual: null` wherever no actual exists - never zero, never
// carried forward. `state` says why (reserve actuals land D-1 evening; GR
// imbalance arrives in weekly ENTSO-E batches 8-17 days later), so an empty
// actual line can be labelled rather than left looking broken.
export async function fetchBalancingForecastVsActual(
  { market, deliveryDate, seriesKey },
  accessToken,
  { signal } = {}
) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");
  if (!market) throw new Error("market is required");

  const url = new URL(`${BASE_URL}/balancing/forecast/vs-actual`);
  url.searchParams.set("market", market);
  if (deliveryDate) url.searchParams.set("delivery_date", deliveryDate);
  if (seriesKey) url.searchParams.set("series_key", seriesKey);

  const res = await fetch(url.toString(), {
    headers: await buildHeaders(accessToken, {
      "Content-Type": "application/json",
    }),
    signal,
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    const error = new Error(text || `HTTP ${res.status}`);
    error.status = res.status;
    throw error;
  }

  // expected: { provider, market, delivery_date, day_age_days,
  //             series: { <series_key>: { state, state_reason, rows: [{ delivery_start,
  //             p10, p50, p90, recommended_p50, actual }], score, score_state, ... } },
  //             missing_series }
  return res.json();
}

export async function fetchBalancingForecastScores(
  { market, seriesKey, days = 30 },
  accessToken,
  { signal } = {}
) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");
  if (!market) throw new Error("market is required");

  const url = new URL(`${BASE_URL}/balancing/forecast/scores`);
  url.searchParams.set("market", market);
  url.searchParams.set("days", String(days));
  if (seriesKey) url.searchParams.set("series_key", seriesKey);

  const res = await fetch(url.toString(), {
    headers: await buildHeaders(accessToken, {
      "Content-Type": "application/json",
    }),
    signal,
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    const error = new Error(text || `HTTP ${res.status}`);
    error.status = res.status;
    throw error;
  }

  // expected: { market, days, series_key, scores: [...] }
  return res.json();
}

// Phase 2 (price-forecast expansion): GR XBID per-MTU forecast. Note the
// payload is FLAT (one run, one row list), unlike the balancing forecast's
// per-series map - XBID has a single target series.
//
// `vintage` selects a rung of the VINTAGE LADDER: the same delivery day is
// forecast at ~17:15 Athens D-1 ("early", knowing DAM and IDA1) and again at
// 23:20 ("late", knowing IDA2 as well). Omitting it means "late" - the
// deployed and more accurate rung - so a caller written before the ladder
// existed gets exactly what it always got.
export async function fetchXbidForecastLatest(
  { market, deliveryDate, vintage },
  accessToken,
  { signal } = {}
) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");
  if (!market) throw new Error("market is required");

  const url = new URL(`${BASE_URL}/xbid/forecast/latest`);
  url.searchParams.set("market", market);
  if (deliveryDate) url.searchParams.set("delivery_date", deliveryDate);
  if (vintage) url.searchParams.set("vintage", vintage);

  const res = await fetch(url.toString(), {
    headers: await buildHeaders(accessToken, {
      "Content-Type": "application/json",
    }),
    signal,
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    const error = new Error(text || `HTTP ${res.status}`);
    error.status = res.status;
    throw error;
  }

  // expected: { provider, market, vintage, delivery_date, run: {...}|null, price_unit, rows: [...] }
  return res.json();
}

// The ladder itself: one entry per rung with its publication time, its gate,
// its backtest MAE (measured on rows every rung can be scored on, so the rungs
// compare) and its live trailing MAE (whatever it has actually been scored on,
// with live_days alongside so a two-day average is not read as a verdict).
export async function fetchXbidForecastLadder(
  { market, deliveryDate, trailingDays = 30 },
  accessToken,
  { signal } = {}
) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");
  if (!market) throw new Error("market is required");

  const url = new URL(`${BASE_URL}/xbid/forecast/ladder`);
  url.searchParams.set("market", market);
  if (deliveryDate) url.searchParams.set("delivery_date", deliveryDate);
  url.searchParams.set("trailing_days", String(trailingDays));

  const res = await fetch(url.toString(), {
    headers: await buildHeaders(accessToken, {
      "Content-Type": "application/json",
    }),
    signal,
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    const error = new Error(text || `HTTP ${res.status}`);
    error.status = res.status;
    throw error;
  }

  // expected: { provider, market, delivery_date, trailing_days, backtest_basis, rungs: [...] }
  return res.json();
}

export async function fetchXbidForecastScores(
  { market, days = 30, vintage },
  accessToken,
  { signal } = {}
) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");
  if (!market) throw new Error("market is required");

  const url = new URL(`${BASE_URL}/xbid/forecast/scores`);
  url.searchParams.set("market", market);
  url.searchParams.set("days", String(days));
  // Omitted means EVERY rung - the comparison is the point of the history.
  if (vintage) url.searchParams.set("vintage", vintage);

  const res = await fetch(url.toString(), {
    headers: await buildHeaders(accessToken, {
      "Content-Type": "application/json",
    }),
    signal,
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    const error = new Error(text || `HTTP ${res.status}`);
    error.status = res.status;
    throw error;
  }

  // expected: { market, days, vintage, scores: [...] }
  return res.json();
}

export async function fetchIntradayPrices(
  { product, market, start, end, series = "actual" },
  accessToken
) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");
  if (!product || !market || !start || !end) {
    throw new Error("product, market, start, and end are required");
  }

  const url = new URL(`${BASE_URL}/intraday/prices`);
  url.searchParams.set("product", product);
  url.searchParams.set("market", market);
  url.searchParams.set("start", start);
  url.searchParams.set("end", end);
  url.searchParams.set("series", series);

  const res = await fetch(url.toString(), {
    headers: await buildHeaders(accessToken, {
      "Content-Type": "application/json",
    }),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `HTTP ${res.status}`);
  }

  const payload = await res.json();
  return Array.isArray(payload) ? payload : payload?.data || [];
}

export async function fetchXbidPrices(
  { market, start, end, duration = 15, sides, assets, technologies },
  accessToken
) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");
  if (!market || !start || !end) {
    throw new Error("market, start, and end are required");
  }

  const url = new URL(`${BASE_URL}/xbid/prices`);
  url.searchParams.set("market", market);
  url.searchParams.set("start", start);
  url.searchParams.set("end", end);
  url.searchParams.set("duration", String(duration));
  if (Array.isArray(sides) && sides.length) url.searchParams.set("sides", sides.join(","));
  if (Array.isArray(assets) && assets.length) url.searchParams.set("assets", assets.join(","));
  if (Array.isArray(technologies) && technologies.length)
    url.searchParams.set("technologies", technologies.join(","));

  const res = await fetch(url.toString(), {
    headers: await buildHeaders(accessToken, {
      "Content-Type": "application/json",
    }),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `HTTP ${res.status}`);
  }

  const payload = await res.json();
  return Array.isArray(payload) ? payload : payload?.data || [];
}

// Per-group XBID series: each selected side/asset/technology group gets its own
// VWAP, Min, Max and volume. Returns { labels: string[], series: [{ delivery_time,
// vwap: {label->€}, min: {label->€}, max: {label->€}, volume: {label->MW} }] }.
export async function fetchXbidCompare(
  { market, start, end, duration = 15, sides, assets, technologies },
  accessToken
) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");
  if (!market || !start || !end) {
    throw new Error("market, start, and end are required");
  }

  const url = new URL(`${BASE_URL}/xbid/compare`);
  url.searchParams.set("market", market);
  url.searchParams.set("start", start);
  url.searchParams.set("end", end);
  url.searchParams.set("duration", String(duration));
  if (Array.isArray(sides) && sides.length) url.searchParams.set("sides", sides.join(","));
  if (Array.isArray(assets) && assets.length) url.searchParams.set("assets", assets.join(","));
  if (Array.isArray(technologies) && technologies.length)
    url.searchParams.set("technologies", technologies.join(","));

  const res = await fetch(url.toString(), {
    headers: await buildHeaders(accessToken, {
      "Content-Type": "application/json",
    }),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `HTTP ${res.status}`);
  }

  const payload = await res.json();
  return payload && typeof payload === "object"
    ? { labels: payload.labels || [], series: payload.series || [] }
    : { labels: [], series: [] };
}

export async function fetchWeatherForecast({ lat, lon, start_date, end_date, tz, type }, accessToken) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");
  if (lat == null || lon == null) throw new Error("lat and lon are required");
  if (!start_date || !end_date) throw new Error("start_date and end_date are required");
  if (!tz) throw new Error("tz is required");
  if (!type) throw new Error("type is required");

  const body = { lat, lon, start_date, end_date, tz, type_: type };

  const res = await fetch(`${BASE_URL}/weather_data`, {
    method: "POST",
    headers: await buildHeaders(accessToken, {
      "Content-Type": "application/json",
      Accept: "application/json",
    }),
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `HTTP ${res.status}`);
  }

  return res.json(); // { weather: [...] } raw rows
}

export async function fetchWindWeather({ lat, lon, start_date, end_date, tz, type }, accessToken) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");
  if (lat == null || lon == null) throw new Error("lat and lon are required");
  if (!start_date || !end_date) throw new Error("start_date and end_date are required");
  if (!tz) throw new Error("tz is required");
  if (!type) throw new Error("type is required");

  const body = { lat, lon, start_date, end_date, tz, type_: type };

  const res = await fetch(`${BASE_URL}/wind_weather_data`, {
    method: "POST",
    headers: await buildHeaders(accessToken, {
      "Content-Type": "application/json",
      Accept: "application/json",
    }),
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `HTTP ${res.status}`);
  }

  // { weather: [{date, temp, wind_speed, pressure, wind_speed_upper?}],
  //   upper_wind_height_m, pressure_source }
  return res.json();
}


export async function getBatteryDetails(name, accessToken) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");
  if (!name) throw new Error("Battery name is required");

  const url = `${BASE_URL}/catalog/batteries/${encodeURIComponent(name)}`;

  const res = await fetch(url, {
    headers: await buildHeaders(accessToken, {
      "Content-Type": "application/json",
    }),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `HTTP ${res.status}`);
  }

  return res.json(); // { name, energy, p_max_c, p_max_d, characteristics: { ... } }
}

/**
 * The measured dispatch-realism (capture) ratio the PLATFORM measured.
 *
 * Returns { market, project, reference, recommended }. `project` is the
 * measurement taken on this project's own battery (the bankable basis, because
 * the ratio is a property of the battery as much as of the forecast);
 * `reference` is the market-wide measurement on a fixed reference battery,
 * available instantly. `recommended` is whichever the form should preselect.
 */
export async function getCaptureRatio({ market, projectId }, accessToken) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");
  if (!market) throw new Error("Market is required");

  const params = new URLSearchParams({ market });
  if (projectId) params.set("project_id", projectId);

  const res = await fetch(`${BASE_URL}/dam/capture-ratio/latest?${params}`, {
    headers: await buildHeaders(accessToken, { "Content-Type": "application/json" }),
  });
  return handleJson(res);
}

/** Reference measurements over time - the drift of the ratio is itself a fact. */
export async function getCaptureRatioHistory({ market, limit = 24 }, accessToken) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");
  if (!market) throw new Error("Market is required");

  const params = new URLSearchParams({ market, limit: String(limit) });
  const res = await fetch(`${BASE_URL}/dam/capture-ratio/history?${params}`, {
    headers: await buildHeaders(accessToken, { "Content-Type": "application/json" }),
  });
  return handleJson(res);
}

/**
 * Measure now. MINUTES, not seconds: one dispatch LP per delivery day, twice,
 * over the whole window. Callers must show progress and must not time out
 * early.
 */
export async function measureCaptureRatio(
  { market, projectId, scope = "project", startDate, endDate, socMode, source },
  accessToken
) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");
  if (!market) throw new Error("Market is required");

  const res = await fetch(`${BASE_URL}/dam/capture-ratio/measure`, {
    method: "POST",
    headers: await buildHeaders(accessToken, { "Content-Type": "application/json" }),
    body: JSON.stringify({
      market,
      scope,
      project_id: projectId ?? null,
      start_date: startDate ?? null,
      end_date: endDate ?? null,
      soc_mode: socMode || "carry",
      source: source ?? null,
    }),
  });
  return handleJson(res);
}

async function handleJson(res) {
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw httpResponseError(res, text || `HTTP ${res.status}`);
  }
  return res.json();
}

/**
 * Delete a catalogue item.
 *
 * The server is the authority on whether this is allowed: it reads the caller's
 * organisation from their profile row (never from the request) and permits the
 * delete only for that organisation's own items, or for a shared item when the
 * caller is an administrator. The UI hides controls it knows will be refused, but
 * that is presentation - it is not the security boundary.
 *
 * Inverters go through a query parameter rather than a path segment because their
 * names contain slashes, matching how getInverterDetails already reads them.
 *
 * Error text is passed through verbatim so the dialog can show the server's own
 * explanation ("shared catalogue item... only an administrator can remove it")
 * rather than a generic failure.
 */
async function deleteCatalogItem(path, accessToken) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");
  const res = await fetch(`${BASE_URL}${path}`, {
    method: "DELETE",
    headers: await buildHeaders(accessToken, { "Content-Type": "application/json" }),
  });
  if (!res.ok) {
    let detail = "";
    try {
      const body = await res.json();
      detail = body?.detail || "";
    } catch {
      detail = await res.text().catch(() => "");
    }
    throw new Error(detail || `HTTP ${res.status}`);
  }
  return res.json();
}

export function deleteModule(name, accessToken) {
  if (!name) throw new Error("Module name is required");
  return deleteCatalogItem(`/catalog/modules/${encodeURIComponent(name)}`, accessToken);
}

export function deleteInverter(name, accessToken) {
  if (!name) throw new Error("Inverter name is required");
  return deleteCatalogItem(`/catalog/inverters?name=${encodeURIComponent(name)}`, accessToken);
}

export function deleteBattery(name, accessToken) {
  if (!name) throw new Error("Battery name is required");
  return deleteCatalogItem(`/catalog/batteries/${encodeURIComponent(name)}`, accessToken);
}

// Several PV panels read different properties of the same catalogue record on
// mount. Share only overlapping reads; a later visit still asks the server so
// edits to the catalogue cannot leave a stale module in the UI.
const pendingModuleDetails = new Map();

export async function getModuleDetails(name, accessToken) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");
  const url = `${BASE_URL}/catalog/modules/${encodeURIComponent(name)}`;
  const headers = await buildHeaders(accessToken, { "Content-Type": "application/json" });
  const key = `${headers.Authorization || ""}\n${url}`;
  const pending = pendingModuleDetails.get(key);
  if (pending) return pending;

  const request = (async () => {
    const res = await fetch(url, { headers });
    return handleJson(res); // { name, characteristics, modeling }
  })();
  pendingModuleDetails.set(key, request);
  try {
    return await request;
  } finally {
    if (pendingModuleDetails.get(key) === request) pendingModuleDetails.delete(key);
  }
}

/**
 * Fit Sandia inverter coefficients from datasheet efficiency curves.
 *
 * Nothing is saved: this is the preview that shows the coefficients and how
 * closely the fitted model reproduces the entered points, so the user can judge
 * the fit before committing the inverter to the catalogue.
 *
 * curves: [{ level: "Vmin"|"Vnom"|"Vmax", dc_voltage, points: [{load, efficiency}] }]
 * All three levels are required — C1..C3 describe the voltage dependence and
 * cannot be fitted from fewer.
 */
/**
 * What each pvlib model needs, so the catalogue form can ask for exactly those
 * fields. Served from pv_core.parameter_sets rather than hardcoded here: the
 * engine chooses its model from whichever coefficients are present, so a list
 * that drifted out of step would invite saving modules the engine then refuses.
 */
/**
 * Terrain (far-shading) profile for the project's coordinates, from PVGIS.
 *
 * Returned rather than stored server-side: the profile belongs in the project
 * config beside the other site inputs, so the caller saves it with the rest.
 */
/**
 * The terrain profile for a site.
 *
 * `lat`/`lon` are the coordinates currently on screen. They matter because the
 * form fetches a horizon the moment the site moves, which is BEFORE the project
 * is saved -- without them the server answers from the stored location and the
 * panel shows the terrain of wherever the site used to be. Omitted, the server
 * falls back to the saved project.
 */
/**
 * Ground elevation for a pair of coordinates. Read-only, never stored.
 *
 * The simulation resolves this itself at run time and requires it -- it feeds
 * `alt2pres` and from there the absolute air mass the spectral correction uses.
 * Showing it lets someone sanity-check the pin; capturing it would create the
 * same drift the horizon profile had to be rescued from.
 */
export async function fetchSiteElevation(lat, lon, accessToken, { signal } = {}) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");
  const res = await fetch(
    `${BASE_URL}/site/elevation?lat=${encodeURIComponent(lat)}&lon=${encodeURIComponent(lon)}`,
    { headers: await buildHeaders(accessToken, { "Content-Type": "application/json" }), signal }
  );
  return handleJson(res);
}

export async function fetchSunPath(lat, lon, profile, accessToken) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");
  // POST, not GET: the horizon profile is up to 360 samples and belongs in a
  // body. It is sent from the form rather than read from the saved project, so
  // a pin that has just moved shows the terrain for where it is now.
  const res = await fetch(`${BASE_URL}/site/sun-path`, {
    method: "POST",
    headers: await buildHeaders(accessToken, { "Content-Type": "application/json" }),
    body: JSON.stringify({ lat, lon, profile: profile || null }),
  });
  return handleJson(res);
}

export async function fetchProjectHorizon(projectId, accessToken, { lat, lon } = {}) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");
  const query =
    lat != null && lon != null
      ? `?lat=${encodeURIComponent(lat)}&lon=${encodeURIComponent(lon)}`
      : "";
  const res = await fetch(
    `${BASE_URL}/projects/${encodeURIComponent(projectId)}/horizon/fetch${query}`,
    {
      method: "POST",
      headers: await buildHeaders(accessToken, { "Content-Type": "application/json" }),
    }
  );
  return handleJson(res);
}

export async function getCatalogParameterSets(accessToken) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");
  const res = await fetch(`${BASE_URL}/catalog/parameter-sets`, {
    headers: await buildHeaders(accessToken, { "Content-Type": "application/json" }),
  });
  return handleJson(res);
}

export async function previewInverterFit({ paco, pnt, curves }, accessToken) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");
  const res = await fetch(`${BASE_URL}/catalog/inverters/fit-preview`, {
    method: "POST",
    headers: await buildHeaders(accessToken, { "Content-Type": "application/json" }),
    body: JSON.stringify({ paco, pnt, curves }),
  });
  return handleJson(res);
}

export async function getModuleIvCurve(name, params = {}, accessToken) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");
  if (!name) throw new Error("Module name is required");

  const search = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value == null || value === "") return;
    search.set(key, String(value));
  });

  const url = `${BASE_URL}/catalog/modules/${encodeURIComponent(name)}/iv-curve${
    search.toString() ? `?${search.toString()}` : ""
  }`;
  const res = await fetch(url, {
    headers: await buildHeaders(accessToken, { "Content-Type": "application/json" }),
  });

  if (!res.ok) {
    const normalizeErrorMessage = (raw) => {
      if (typeof raw === "string") return raw;
      if (Array.isArray(raw)) {
        const joined = raw
          .map((item) => normalizeErrorMessage(item))
          .filter(Boolean)
          .join("; ");
        return joined || null;
      }
      if (raw && typeof raw === "object") {
        return (
          raw.message ||
          raw.detail ||
          raw.error ||
          (raw.loc && raw.msg ? `${raw.loc.join(".")}: ${raw.msg}` : null) ||
          JSON.stringify(raw)
        );
      }
      return null;
    };

    let message = `HTTP ${res.status}`;
    try {
      const data = await res.json();
      message =
        normalizeErrorMessage(data?.detail) ||
        normalizeErrorMessage(data?.message) ||
        normalizeErrorMessage(data?.error) ||
        normalizeErrorMessage(data) ||
        message;
    } catch {
      const text = await res.text().catch(() => "");
      if (text) message = text;
    }
    const err = new Error(message);
    err.status = res.status;
    throw err;
  }

  return res.json();
}

export async function getInverterDetails(name, accessToken) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");
  if (!name) throw new Error("Inverter name is required");

  const url = `${BASE_URL}/catalog/inverter_details?name=${encodeURIComponent(
    name
  )}`;

  const res = await fetch(url, {
    headers: await buildHeaders(accessToken, { "Content-Type": "application/json" }),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw httpResponseError(res, text || `HTTP ${res.status}`);
  }

  return res.json();
}

export async function updateOndConnection(name, connection, accessToken) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");
  const res = await fetch(`${BASE_URL}/catalog/inverters/ond-connection?name=${encodeURIComponent(name)}`, {
    method: "PUT",
    headers: await buildHeaders(accessToken, { "Content-Type": "application/json" }),
    body: JSON.stringify({ connection }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : `HTTP ${res.status}`);
  return data;
}

export async function getInverterDesign(name, accessToken, { moduleName, maxCellC } = {}) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");
  if (!name) throw new Error("Inverter name is required");

  const query = new URLSearchParams({ name });
  if (moduleName) query.set("module_name", moduleName);
  if (maxCellC != null) query.set("max_cell_c", String(maxCellC));
  const url = `${BASE_URL}/catalog/inverter_design?${query}`;

  const res = await fetch(url, {
    headers: await buildHeaders(accessToken, {
      "Content-Type": "application/json",
    }),
  });

  return handleJson(res);
}

export async function validatePvDesign(payload, accessToken) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");
  const controller = new AbortController();
  let timedOut = false;
  const timeout = setTimeout(() => { timedOut = true; controller.abort(); }, 15000);
  try {
    const res = await fetch(`${BASE_URL}/design/validate`, {
      method: "POST",
      headers: await buildHeaders(accessToken, { "Content-Type": "application/json" }),
      body: JSON.stringify(payload),
      signal: controller.signal,
    });
    if (!res.ok) {
      throw httpResponseError(res, "Electrical design check could not complete.");
    }
    return await res.json();
  } catch (error) {
    if (timedOut && error?.name === "AbortError") {
      throw Object.assign(new Error("Electrical design check timed out."), { code: "REQUEST_TIMEOUT" });
    }
    throw error;
  } finally {
    clearTimeout(timeout);
  }
}

export async function createBattery(payload, accessToken) {
  const res = await fetch(`${BASE_URL}/catalog/batteries`, {
    method: "POST",
    headers: await buildHeaders(accessToken, {
      "Content-Type": "application/json",
    }),
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    throw new Error(await res.text());
  }

  return await res.json();
}

export async function previewPanModule(file, accessToken) {
  const body = new FormData();
  body.append("file", file);
  const res = await fetch(`${BASE_URL}/catalog/modules/import-pan`, {
    method: "POST",
    headers: await buildHeaders(accessToken),
    body,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "The PAN file could not be imported.");
  return data;
}

// A PVsyst OND file, read on the server into the inverter it describes. Nothing
// is saved: the preview's characteristics go back with the Add-inverter save,
// which rebuilds them from the stored file text.
export async function previewOndInverter(file, accessToken) {
  const body = new FormData();
  body.append("file", file);
  const res = await fetch(`${BASE_URL}/catalog/inverters/import-ond`, {
    method: "POST",
    headers: await buildHeaders(accessToken),
    body,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "The OND file could not be imported.");
  return data;
}

export async function getCatalogOrganizations(accessToken) {
  const res = await fetch(`${BASE_URL}/catalog/organizations`, {
    headers: await buildHeaders(accessToken),
  });
  if (!res.ok) throw new Error("Could not load organizations.");
  return res.json();
}

export async function createModule(payload, accessToken) {
  const res = await fetch(`${BASE_URL}/catalog/modules`, {
    method: "POST",
    headers: await buildHeaders(accessToken, {
      "Content-Type": "application/json",
    }),
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data?.detail || "Failed to create module");
  }
  return res.json();
}

export async function createInverter(payload, accessToken) {
  const res = await fetch(`${BASE_URL}/catalog/inverters`, {
    method: "POST",
    headers: await buildHeaders(accessToken, {
      "Content-Type": "application/json",
    }),
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data?.detail || "Failed to create inverter");
  }
  return res.json();
}

// ---------------------------------------------------------------------------
// TMY import (user-supplied weather data)
// ---------------------------------------------------------------------------
async function tmyJson(res) {
  const text = await res.text().catch(() => "");
  let payload = null;
  try { payload = text ? JSON.parse(text) : null; } catch { payload = null; }
  if (!res.ok) {
    // 422 carries {detail: {report}} with validation errors for display.
    const report = payload?.detail?.report;
    const err = new Error(
      report?.errors?.join(" ") || payload?.detail || text || `HTTP ${res.status}`
    );
    err.status = res.status;
    err.report = report || null;
    throw err;
  }
  return payload;
}

export async function importProjectTmy(projectId, file, accessToken) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");
  const form = new FormData();
  form.append("file", file, file.name);
  const res = await fetch(`${BASE_URL}/projects/${projectId}/tmy/import`, {
    method: "POST",
    headers: await buildHeaders(accessToken), // no Content-Type: browser sets multipart boundary
    body: form,
  });
  return tmyJson(res); // { import, report }
}

export async function listProjectTmy(projectId, accessToken) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");
  const res = await fetch(`${BASE_URL}/projects/${projectId}/tmy`, {
    method: "GET",
    headers: await buildHeaders(accessToken, { Accept: "application/json" }),
  });
  return tmyJson(res); // { imports: [...] }
}

export async function activateProjectTmy(projectId, tmyId, assetType, active, accessToken) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");
  const res = await fetch(`${BASE_URL}/projects/${projectId}/tmy/${tmyId}/activate`, {
    method: "POST",
    headers: await buildHeaders(accessToken, { "Content-Type": "application/json" }),
    body: JSON.stringify({ asset_type: assetType, active }),
  });
  return tmyJson(res);
}

export async function deleteProjectTmy(projectId, tmyId, accessToken) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");
  const res = await fetch(`${BASE_URL}/projects/${projectId}/tmy/${tmyId}`, {
    method: "DELETE",
    headers: await buildHeaders(accessToken),
  });
  return tmyJson(res);
}

export async function downloadTmyTemplate(accessToken) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");
  const res = await fetch(`${BASE_URL}/tmy/template`, {
    method: "GET",
    headers: await buildHeaders(accessToken),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "TMY_template.csv";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/**
 * Download an imported dataset.
 *
 * variant "normalized" (default) returns the canonical hourly CSV — the exact
 * rows the simulation reads, with derived DNI/DHI and substituted constants
 * materialised. variant "raw" returns the uploaded file byte-for-byte, which is
 * the only way to audit the import itself.
 *
 * The backend chooses the filename; `filename` here is only a fallback.
 */
export async function downloadProjectTmy(
  projectId,
  tmyId,
  accessToken,
  filename,
  variant = "normalized"
) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");
  if (!projectId) throw new Error("projectId is required");
  const res = await fetch(
    `${BASE_URL}/projects/${projectId}/tmy/${tmyId}/download?variant=${encodeURIComponent(variant)}`,
    {
      method: "GET",
      headers: await buildHeaders(accessToken),
    }
  );
  if (!res.ok) {
    // Surface the backend's explanation (e.g. raw file not retained) rather
    // than a bare status code.
    let detail = "";
    try {
      const body = await res.json();
      detail = body?.detail || "";
    } catch {
      /* non-JSON error body */
    }
    throw new Error(detail || `HTTP ${res.status}`);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "TMY_normalized.csv";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export async function fetchImportedTmyWeather({ projectId, asset, start_date, end_date, tz }, accessToken) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");
  if (!projectId) throw new Error("projectId is required");
  const res = await fetch(`${BASE_URL}/projects/${projectId}/tmy/weather_data`, {
    method: "POST",
    headers: await buildHeaders(accessToken, {
      "Content-Type": "application/json",
      Accept: "application/json",
    }),
    body: JSON.stringify({ asset_type: asset, start_date, end_date, tz }),
  });
  return tmyJson(res); // { weather: [...], source: "imported_tmy" }
}
/**
 * Weather sources the backend can fetch server-side.
 *
 * Drives the source picker rather than a hardcoded list, so a provider added on
 * the backend appears here without a frontend release. Each entry carries
 * `requires_api_key`, `needs_year`, `year_range`, `available` and
 * `unavailable_reason`, which is everything the form needs to decide what to
 * ask for. Carries no credentials.
 */
export async function listTmyProviders(accessToken) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");
  const res = await fetch(`${BASE_URL}/tmy/providers`, {
    method: "GET",
    headers: await buildHeaders(accessToken, { Accept: "application/json" }),
  });
  return tmyJson(res); // { providers: [...], default, latest_complete_year }
}

/**
 * Fetch a weather year for the project's coordinates from `provider` and store
 * it like an uploaded dataset.
 *
 * `year` applies only to providers that serve dated years rather than a typical
 * year (CAMS, Solargis); omit it to take the most recent complete year.
 *
 * `apiKey` is the user's OWN subscription key for commercial providers. It is
 * sent for this one request and is deliberately not cached here, not written to
 * storage, and not retained after the call — the caller is expected to hold it
 * in transient component state only. The backend likewise never persists it and
 * suppresses provider error text for keyed providers, because some upstream
 * clients echo the key back inside the failing request URL.
 */
export async function fetchProjectTmyFromProvider(
  projectId,
  provider,
  accessToken,
  { year = null, apiKey = null } = {},
) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");
  const body = { provider };
  if (year != null && year !== "") body.year = Number(year);
  // Only include the key when one was actually supplied: sending api_key: null
  // for PVGIS/CAMS would put a credential field on requests that have no
  // business carrying one.
  const trimmedKey = String(apiKey ?? "").trim();
  if (trimmedKey) body.api_key = trimmedKey;

  const res = await fetch(`${BASE_URL}/projects/${projectId}/tmy/fetch`, {
    method: "POST",
    headers: await buildHeaders(accessToken, { "Content-Type": "application/json" }),
    body: JSON.stringify(body),
  });
  return tmyJson(res); // { import, report }
}

/**
 * PVGIS typical meteorological year for a coordinate, as a CSV blob.
 *
 * Takes no project on purpose: this downloads a file and changes nothing. The
 * project's weather is chosen on the Weather page, where the choice is recorded
 * against the project -- a download that silently became the model's input is
 * exactly the kind of mismatch that is impossible to notice later.
 */
export async function downloadPvgisTmy({ lat, lon }, accessToken) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");
  const res = await fetch(`${BASE_URL}/tmy/pvgis/export`, {
    method: "POST",
    headers: await buildHeaders(accessToken, { "Content-Type": "application/json" }),
    body: JSON.stringify({ lat, lon }),
  });
  if (!res.ok) throw new Error((await res.text()) || "PVGIS request failed");
  return {
    blob: await res.blob(),
    filename:
      filenameFromContentDisposition(res.headers.get("Content-Disposition")) ||
      "PVGIS_TMY.csv",
  };
}

export async function fetchProjectTmyProfile(projectId, tmyId, accessToken) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");
  const res = await fetch(`${BASE_URL}/projects/${projectId}/tmy/${tmyId}/profile`, {
    method: "GET",
    headers: await buildHeaders(accessToken, { Accept: "application/json" }),
  });
  return tmyJson(res); // { monthly, daily, diurnal, annual, ... }
}

/**
 * Same aggregates for the platform's own weather, so the Weather page can
 * inspect the default source instead of only the imported files. Returns the
 * same envelope as fetchProjectTmyProfile, plus { kind: "platform", year }.
 * Slower than the stored-dataset profile: it fetches a full year on demand.
 */
export async function fetchPlatformWeatherProfile(
  { projectId, lat, lon, tz, year, type = "historical" },
  accessToken
) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");
  if (!projectId) throw new Error("projectId is required");
  if (lat == null || lon == null) throw new Error("lat and lon are required");
  if (!tz) throw new Error("tz is required");
  if (!year) throw new Error("year is required");
  const res = await fetch(`${BASE_URL}/projects/${projectId}/tmy/platform_profile`, {
    method: "POST",
    headers: await buildHeaders(accessToken, {
      "Content-Type": "application/json",
      Accept: "application/json",
    }),
    body: JSON.stringify({ lat, lon, tz, year, type_: type }),
  });
  return tmyJson(res);
}

/**
 * Annual irradiation per year for the project's coordinates, so a single real
 * year can be judged against its neighbours. Returns
 * { years: [{year, ghi_kwh_m2, deviation_pct, ...}], mean_ghi_kwh_m2 }.
 */
export async function fetchPlatformTypicality(
  { projectId, lat, lon, tz, years, type = "historical" },
  accessToken
) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");
  if (!projectId) throw new Error("projectId is required");
  if (lat == null || lon == null) throw new Error("lat and lon are required");
  if (!tz) throw new Error("tz is required");
  if (!Array.isArray(years) || !years.length) throw new Error("years is required");
  const res = await fetch(`${BASE_URL}/projects/${projectId}/tmy/platform_typicality`, {
    method: "POST",
    headers: await buildHeaders(accessToken, {
      "Content-Type": "application/json",
      Accept: "application/json",
    }),
    body: JSON.stringify({ lat, lon, tz, years, type_: type }),
  });
  return tmyJson(res);
}

/**
 * Download the platform's weather for a year as a CSV, in the import template
 * format so the file can be fed straight back in. Mirrors downloadProjectTmy,
 * which only covers uploaded datasets.
 */
export async function downloadPlatformWeather(
  { projectId, lat, lon, tz, year, type = "historical" },
  accessToken
) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");
  if (!projectId) throw new Error("projectId is required");
  if (lat == null || lon == null) throw new Error("lat and lon are required");
  if (!tz) throw new Error("tz is required");
  if (!year) throw new Error("year is required");
  const res = await fetch(`${BASE_URL}/projects/${projectId}/tmy/platform_download`, {
    method: "POST",
    headers: await buildHeaders(accessToken, { "Content-Type": "application/json" }),
    body: JSON.stringify({ lat, lon, tz, year, type_: type }),
  });
  if (!res.ok) {
    let detail = "";
    try {
      const body = await res.json();
      detail = body?.detail || "";
    } catch {
      /* non-JSON error body */
    }
    throw new Error(detail || `HTTP ${res.status}`);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `platform_weather_${year}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/* ------------------------------------------------------------------ *
 * The stored TMY estimate
 *
 * One simulation per project, kept: the two workbook downloads read it
 * rather than each re-running the annual model, which is what they used
 * to do - twice the compute, and two files that could describe different
 * systems if anything changed between the clicks.
 * ------------------------------------------------------------------ */
export async function runTmyEstimate(projectId, config, accessToken) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");
  if (!projectId) throw new Error("Open or save a project before running an estimate");

  const payload = await productionSceneRequest(config, {
    projectId, loadModule: (name) => getModuleDetails(name, accessToken),
  });

  const res = await fetch(
    `${BASE_URL}/pv/tmy/estimate/${encodeURIComponent(projectId)}`,
    {
      method: "POST",
      headers: await buildHeaders(accessToken, { "Content-Type": "application/json" }),
      body: JSON.stringify(payload),
    }
  );
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    const detail = body?.detail;
    const message = typeof detail === "string" ? detail : detail?.message;
    const error = new Error(message || `Estimate failed (${res.status})`);
    error.code = detail?.code;
    error.technicalDetail = detail?.technical_detail;
    throw error;
  }
  return res.json();
}

export async function fetchTmyEstimate(projectId, accessToken) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");
  if (!projectId) return null;

  const res = await fetch(
    `${BASE_URL}/pv/tmy/estimate/${encodeURIComponent(projectId)}`,
    { headers: await buildHeaders(accessToken) }
  );
  if (res.status === 404) return null;          // never run: not an error
  if (!res.ok) {
    const msg = await res.text().catch(() => "");
    throw new Error(msg || `Could not load the stored estimate (${res.status})`);
  }
  return res.json();
}

export async function fetchTmyEstimationJob(projectId, accessToken) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");
  const res = await fetch(
    `${BASE_URL}/pv/tmy/estimate/${encodeURIComponent(projectId)}/job`,
    { headers: await buildHeaders(accessToken) }
  );
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    const error = new Error(body?.detail || `Could not load estimation status (${res.status})`);
    error.status = res.status;
    throw error;
  }
  return (await res.json()).job || null;
}

export async function fetchConfigFingerprint(config, accessToken, referenceFingerprint, projectId) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");
  // Hashed server-side on purpose: two implementations of the same digest is
  // how a staleness check starts lying quietly.
  const res = await fetch(`${BASE_URL}/pv/tmy/estimate/fingerprint`, {
    method: "POST",
    headers: await buildHeaders(accessToken, { "Content-Type": "application/json" }),
    body: JSON.stringify({ config, reference_fingerprint: referenceFingerprint, project_id: projectId }),
  });
  if (!res.ok) return null;
  const data = await res.json().catch(() => null);
  return data?.fingerprint || null;
}

export async function downloadTmyWorkbook(projectId, kind, accessToken) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");
  if (!projectId) throw new Error("projectId is required");

  const res = await fetch(
    `${BASE_URL}/pv/tmy/estimate/${encodeURIComponent(projectId)}/download/${encodeURIComponent(kind)}`,
    { headers: await buildHeaders(accessToken) }
  );
  if (res.status === 404) {
    const err = new Error("Run Estimate first - there is no stored workbook yet");
    err.status = 404;
    throw err;
  }
  if (!res.ok) {
    const msg = await res.text().catch(() => "");
    throw new Error(msg || `Download failed (${res.status})`);
  }
  const blob = await res.blob();
  const filename =
    filenameFromContentDisposition(res.headers?.get("content-disposition")) ||
    (kind === "losses" ? "PV_Losses.xlsx" : kind === "weather" ? "Weather_Year_Hourly.csv" : "PV_Generation_Overview.xlsx");
  return { blob, filename };
}

/**
 * The stored estimate as a PDF: the same numbers the page shows, as a document.
 * The server refuses (409) when the stored estimate no longer matches the
 * settings in hand, which the page also shows as "stale".
 */
export async function downloadTmyReport(projectId, config, projectName, accessToken, reportBranding) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");
  if (!projectId) throw new Error("projectId is required");

  const res = await fetch(
    `${BASE_URL}/pv/tmy/estimate/${encodeURIComponent(projectId)}/report.pdf`,
    {
      method: "POST",
      headers: await buildHeaders(accessToken, { "Content-Type": "application/json" }),
      body: JSON.stringify({ config, project_name: projectName || null, report_branding: reportBranding, report_language: getReportLanguage(config) }),
    }
  );
  if (res.status === 404) {
    const err = new Error("Run Estimate first - there is no stored estimate yet");
    err.status = 404;
    throw err;
  }
  if (res.status === 409) {
    // Changed settings alone no longer produce a 409: the report is of the
    // stored run and downloads regardless. What remains is the narrow case of
    // an estimate stored before the plant recorded its own mounting, where the
    // document would have mixed the old energy with the current design.
    const err = new Error("This estimate was stored by an earlier version and the settings have changed since. Run Estimate again, then download the report.");
    err.status = 409;
    throw err;
  }
  if (!res.ok) {
    let msg = "";
    try { msg = (await res.json())?.detail || ""; } catch { msg = await res.text().catch(() => ""); }
    throw new Error(msg || `The report could not be produced (${res.status})`);
  }
  const blob = await res.blob();
  const filename =
    filenameFromContentDisposition(res.headers?.get("content-disposition")) ||
    "PV generation estimate.pdf";
  return { blob, filename };
}

export async function fetchLayoutPdf(payload, accessToken = null) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");
  // The layout sheet is deliberately NOT tied to a project id or a run: it is
  // built from the design the layout tab is holding, which is the whole point
  // of it existing separately from the feasibility report.
  const res = await fetch(`${BASE_URL}/pv/layout.pdf`, {
    method: "POST",
    headers: await buildHeaders(accessToken, { "Content-Type": "application/json" }),
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    let message = `HTTP ${res.status}`;
    try {
      const body = await res.json();
      if (body?.detail) message = String(body.detail);
    } catch {}
    throw new Error(message);
  }
  const blob = await res.blob();
  const filename =
    filenameFromContentDisposition(res.headers?.get("content-disposition")) ||
    "PV system design.pdf";
  return { blob, filename };
}


/**
 * Rows fitted inside the parcel at the design pitch and azimuth.
 * Geometry only, on the server, so the layout tab, the sheet and the string
 * map all read one answer.
 */
export async function analyzeParcel(body, accessToken = null) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");
  const res = await fetch(`${BASE_URL}/site/parcel/analyze`, {
    method: "POST",
    headers: await buildHeaders(accessToken, { "Content-Type": "application/json" }),
    body: JSON.stringify(body),
  });
  return handleJson(res);
}

/** Ground elevations sampled across the parcel and the plane through them. */
export async function fetchParcelTerrain(polygon, accessToken = null) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");
  const res = await fetch(`${BASE_URL}/site/parcel/terrain`, {
    method: "POST",
    headers: await buildHeaders(accessToken, { "Content-Type": "application/json" }),
    body: JSON.stringify({ polygon }),
  });
  return handleJson(res);
}

/** Electrically checked string sizing for the modules the edited plan holds. */
export async function fitStringsToPlan(body, accessToken = null) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");
  const res = await fetch(`${BASE_URL}/pv/strings/fit`, {
    method: "POST",
    headers: await buildHeaders(accessToken, { "Content-Type": "application/json" }),
    body: JSON.stringify(body),
  });
  return handleJson(res);
}

/** Every string placed on a row, with hand placements honoured and checked. */
export async function fetchStringLayout(body, accessToken = null) {
  if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set");
  const res = await fetch(`${BASE_URL}/pv/string-layout`, {
    method: "POST",
    headers: await buildHeaders(accessToken, { "Content-Type": "application/json" }),
    body: JSON.stringify(body),
  });
  return handleJson(res);
}
